import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Paintbrush,
  Package,
  Clock,
  Building2,
  Users,
  RefreshCw,
  Printer,
  Layers,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

// ---------- helpers ----------
const urgencyFor = (daysOut, thresholds) => {
  const g = thresholds?.green_days ?? 3;
  const y = thresholds?.yellow_days ?? 7;
  if (daysOut == null) return "green";
  if (daysOut <= g) return "green";
  if (daysOut <= y) return "yellow";
  return "red";
};

const urgencyClasses = (u) => {
  switch (u) {
    case "red":
      return {
        border: "border-l-[#D66853]",
        chip: "bg-[#D66853]/15 text-[#a44231] border-[#D66853]/40",
        row: "bg-[#D66853]/5",
      };
    case "yellow":
      return {
        border: "border-l-[#E5A93D]",
        chip: "bg-[#E5A93D]/15 text-[#7c5a14] border-[#E5A93D]/40",
        row: "bg-[#E5A93D]/5",
      };
    default:
      return {
        border: "border-l-primary",
        chip: "bg-primary/10 text-primary border-primary/30",
        row: "",
      };
  }
};

function VendorBadge({ type, notes }) {
  if (!type && !notes) return null;
  const external = type === "external";
  const Icon = external ? Building2 : Users;
  const cls = external
    ? "bg-[#D66853]/10 text-[#a44231] border-[#D66853]/30"
    : "bg-primary/10 text-primary border-primary/30";
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border ${cls}`}
      title={notes || ""}
    >
      <Icon className="w-3 h-3" />
      {notes ? notes : external ? "External" : "In-house"}
    </span>
  );
}

function UrgencyChip({ days, thresholds }) {
  const u = urgencyFor(days, thresholds);
  const { chip } = urgencyClasses(u);
  const label =
    days == null
      ? "—"
      : days === 0
      ? "today"
      : days === 1
      ? "1 day"
      : `${days} days`;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border ${chip}`}>
      <Clock className="w-3 h-3" />
      {label}
    </span>
  );
}

// ---------- QUEUE TAB ----------
function QueueTab({ data, thresholds, groupBy, onRefresh }) {
  const entries = data?.entries || [];

  // Build groups: by batch when groupBy=batch, else one bucket
  const groups = useMemo(() => {
    if (groupBy !== "batch") {
      return [
        {
          key: "all",
          label: `All (${entries.length})`,
          batch_number: null,
          rows: entries,
        },
      ];
    }
    const map = new Map();
    for (const e of entries) {
      const key = e.paint_batch_id || `solo:${e.item_id}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          batch_id: e.paint_batch_id,
          batch_number: e.paint_batch_number,
          label: e.paint_batch_number || "Single scan",
          rows: [],
        });
      }
      map.get(key).rows.push(e);
    }
    return Array.from(map.values()).sort((a, b) => {
      const da = Math.max(...a.rows.map((r) => r.days_out || 0));
      const db = Math.max(...b.rows.map((r) => r.days_out || 0));
      return db - da;
    });
  }, [entries, groupBy]);

  const totalOut = entries.reduce((s, e) => s + ((e.units_out || 0) - (e.units_returned || 0)), 0);
  const overdue = entries.filter((e) => urgencyFor(e.days_out, thresholds) === "red").length;
  const watch = entries.filter((e) => urgencyFor(e.days_out, thresholds) === "yellow").length;

  return (
    <div>
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="paint-kpis">
        <div className="surface-card p-4">
          <div className="label-eyebrow">Items at painter</div>
          <div className="font-heading text-2xl font-bold mt-1 num">{entries.length}</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {totalOut} unit{totalOut === 1 ? "" : "s"} tracked
          </div>
        </div>
        <div className="surface-card p-4">
          <div className="label-eyebrow">On track</div>
          <div className="font-heading text-2xl font-bold mt-1 num text-primary">
            {entries.length - overdue - watch}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            ≤ {thresholds?.green_days ?? 3} days out
          </div>
        </div>
        <div className="surface-card p-4">
          <div className="label-eyebrow">Watch</div>
          <div className="font-heading text-2xl font-bold mt-1 num text-[#7c5a14]">
            {watch}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {(thresholds?.green_days ?? 3) + 1}–{thresholds?.yellow_days ?? 7} days
          </div>
        </div>
        <div
          className={`surface-card p-4 ${overdue > 0 ? "bg-[#D66853]/5 border-[#D66853]/30" : ""}`}
        >
          <div className="label-eyebrow">Overdue</div>
          <div
            className={`font-heading text-2xl font-bold mt-1 num ${overdue > 0 ? "text-[#a44231]" : "text-muted-foreground"}`}
          >
            {overdue}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {"> "}
            {thresholds?.yellow_days ?? 7} days at painter
          </div>
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="surface-card p-10 text-center text-muted-foreground">
          <Paintbrush className="w-8 h-8 mx-auto mb-3 opacity-40" strokeWidth={1.5} />
          <div className="font-medium">Nothing at the painter right now</div>
          <div className="text-xs mt-1">
            Items scanned out for paint will appear here in real time.
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.key} className="surface-card overflow-hidden">
              {groupBy === "batch" && (
                <div className="px-4 py-2.5 border-b border-border/60 bg-secondary/40 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <Layers className="w-4 h-4 text-muted-foreground" />
                    <span className="font-mono text-xs">
                      {g.batch_number || "Individual scans"}
                    </span>
                    <span className="text-muted-foreground">· {g.rows.length} items</span>
                  </div>
                  {g.batch_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const backend = process.env.REACT_APP_BACKEND_URL || "";
                        window.open(
                          `${backend}/api/paint/batches/${g.batch_id}/manifest`,
                          "_blank"
                        );
                      }}
                      data-testid={`paint-manifest-${g.batch_id}`}
                    >
                      <Printer className="w-3.5 h-3.5 mr-1.5" /> Manifest
                    </Button>
                  )}
                </div>
              )}
              <table className="w-full text-sm">
                <thead className="bg-secondary/30 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2">Order</th>
                    <th className="text-left px-4 py-2">Item</th>
                    <th className="text-left px-4 py-2">Vendor</th>
                    <th className="text-center px-4 py-2">Units out</th>
                    <th className="text-left px-4 py-2">Days out</th>
                    <th className="text-left px-4 py-2">Delivery</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {g.rows.map((r) => {
                    const cls = urgencyClasses(urgencyFor(r.days_out, thresholds));
                    const currentlyOut = (r.units_out || 0) - (r.units_returned || 0);
                    return (
                      <tr
                        key={`${r.order_id}-${r.item_id}`}
                        className={`hover:bg-secondary/30 border-l-2 ${cls.border} ${cls.row}`}
                        data-testid={`paint-row-${r.item_id}`}
                      >
                        <td className="px-4 py-2.5">
                          <Link
                            to={`/orders/${r.order_id}`}
                            className="font-mono text-xs text-primary hover:underline"
                          >
                            {r.order_number}
                          </Link>
                          <div className="text-xs text-muted-foreground truncate max-w-[180px]">
                            {r.customer_name}
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="font-medium truncate max-w-[280px]">{r.name}</div>
                          <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                            <Package className="w-3 h-3" />
                            <span className="font-mono">{r.sku}</span>
                            {r.color && (
                              <span className="ml-1 inline-block px-1.5 py-0.5 rounded bg-[#D66853]/10 text-[#a44231] text-[10px]">
                                {r.color}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <VendorBadge type={r.vendor_type} notes={r.vendor_notes} />
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span className="num font-semibold">{currentlyOut}</span>
                          <span className="text-xs text-muted-foreground">/{r.units_total}</span>
                        </td>
                        <td className="px-4 py-2.5">
                          <UrgencyChip days={r.days_out} thresholds={thresholds} />
                        </td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground">
                          {r.delivery_date
                            ? new Date(r.delivery_date).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- BATCHES TAB ----------
function BatchesTab() {
  const [batches, setBatches] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (status !== "all") params.status = status;
      const { data } = await api.get("/paint/batches", { params });
      setBatches(data.batches || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [status]);

  const openManifest = (batchId) => {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    window.open(`${backend}/api/paint/batches/${batchId}/manifest`, "_blank");
  };

  return (
    <div>
      <div className="mb-4 flex items-end justify-between gap-3">
        <div>
          <div className="label-eyebrow">Status</div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[180px] mt-1" data-testid="batches-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="received">Received</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="surface-card overflow-hidden">
        <table className="w-full text-sm" data-testid="batches-table">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-2.5">Batch</th>
              <th className="text-left px-4 py-2.5">Vendor</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-center px-4 py-2.5">Items</th>
              <th className="text-left px-4 py-2.5">Created</th>
              <th className="text-left px-4 py-2.5">Received</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && batches.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground italic">
                  No batches match this filter
                </td>
              </tr>
            )}
            {batches.map((b) => {
              const total = (b.items || []).reduce((s, i) => s + (i.units || 0), 0);
              const cls =
                b.status === "received"
                  ? "bg-primary/10 text-primary border-primary/30"
                  : b.status === "partial"
                  ? "bg-[#E5A93D]/15 text-[#7c5a14] border-[#E5A93D]/40"
                  : "bg-[#3d7a8c]/10 text-[#1f5060] border-[#3d7a8c]/30";
              return (
                <tr key={b.id} className="hover:bg-secondary/30">
                  <td className="px-4 py-2.5">
                    <div className="font-mono text-xs">{b.batch_number}</div>
                    {b.notes && (
                      <div className="text-xs text-muted-foreground mt-0.5 truncate max-w-[240px]">
                        {b.notes}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <VendorBadge type={b.vendor_type} notes={b.vendor_notes} />
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded border capitalize ${cls}`}>
                      {b.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-center num">
                    {(b.items || []).length}
                    <span className="text-xs text-muted-foreground"> · {total}u</span>
                  </td>
                  <td className="px-4 py-2.5 text-xs">
                    {b.created_at
                      ? new Date(b.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                      : "—"}
                    <div className="text-[10px] text-muted-foreground">{b.created_by_name || ""}</div>
                  </td>
                  <td className="px-4 py-2.5 text-xs">
                    {b.received_at
                      ? new Date(b.received_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                      : "—"}
                    <div className="text-[10px] text-muted-foreground">{b.received_by_name || ""}</div>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openManifest(b.id)}
                      data-testid={`batch-manifest-${b.id}`}
                    >
                      <Printer className="w-3.5 h-3.5 mr-1.5" /> Manifest
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- MAIN ----------
export default function PaintBoard() {
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groupBy, setGroupBy] = useState("batch");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/paint/queue");
      setQueue(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to reach scanner API");
      setQueue(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const thresholds = queue?.thresholds;

  return (
    <div className="p-6 lg:p-10 animate-fade-in">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-eyebrow">Paint tracking</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            Paint board
          </h1>
          <p className="text-muted-foreground mt-1">
            Live from the scanner app. Refreshes every 30s.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="hidden md:flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Group</span>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger className="w-[140px] h-9" data-testid="paint-group-by">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="batch">By batch</SelectItem>
                <SelectItem value="none">Flat list</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="paint-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="surface-card p-6 bg-[#D66853]/5 border-[#D66853]/30" data-testid="paint-error">
          <div className="flex items-center gap-3">
            <ExternalLink className="w-5 h-5 text-[#a44231]" />
            <div>
              <div className="font-semibold text-[#a44231]">
                Cannot reach scanner API
              </div>
              <div className="text-sm text-muted-foreground mt-1">{error}</div>
            </div>
          </div>
        </div>
      )}

      {!error && (
        <Tabs defaultValue="queue">
          <TabsList className="mb-6">
            <TabsTrigger value="queue" data-testid="paint-tab-queue">
              <Paintbrush className="w-3.5 h-3.5 mr-1.5" /> Queue
            </TabsTrigger>
            <TabsTrigger value="batches" data-testid="paint-tab-batches">
              <Layers className="w-3.5 h-3.5 mr-1.5" /> Batches
            </TabsTrigger>
          </TabsList>
          <TabsContent value="queue">
            <QueueTab
              data={queue}
              thresholds={thresholds}
              groupBy={groupBy}
              onRefresh={load}
            />
          </TabsContent>
          <TabsContent value="batches">
            <BatchesTab />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
